import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, ChipToggle, Field, Input, useToast } from '@/components/ui';
import type { PanelContract } from '@/features/admin-v2/api';
import { useMe } from '@/features/auth/session-context';
import { api, isApiError } from '@/lib/api';
import local from './finance.module.css';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const text = (n: number | null) => (n === null ? '' : String(n));

/** Whole number in [min, max], or null when empty; undefined when invalid. */
function whole(v: string, min: number, max: number): number | null | undefined {
  if (!v.trim()) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
}

/**
 * RL-1758 / RL-1759 (gap #31, decision D21): Finance edits a panel's
 * contract: claim window, payment terms, the monthly invoice (day, review or
 * automatic, reminders), the submission method, the clinic code at each
 * branch, exclusions, the billing email and a company's allowance.
 */
export function PanelContractForm({
  panel,
  onDone,
}: {
  panel: PanelContract;
  onDone: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [windowDays, setWindow] = useState(text(panel.windowDays));
  const [termsDays, setTerms] = useState(text(panel.termsDays));
  const [invoiceDay, setInvoiceDay] = useState(text(panel.invoiceDay));
  const [invoiceMode, setMode] = useState(panel.invoiceMode || 'review');
  const [reminder, setReminder] = useState(String(panel.reminderDaysBefore));
  const [method, setMethod] = useState(panel.submissionMethod || 'portal');
  const [codes, setCodes] = useState<Record<string, string>>(
    panel.clinicCodes ?? {},
  );
  const [exclusions, setExclusions] = useState(panel.exclusions ?? '');
  const [email, setEmail] = useState(panel.contactEmail ?? '');
  const [allowance, setAllowance] = useState(
    panel.allowanceSen === null ? '' : (panel.allowanceSen / 100).toFixed(2),
  );
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<PanelContract>(`/finance/panels/${panel.id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });

  const submit = () => {
    const w = whole(windowDays, 1, 365);
    const t = whole(termsDays, 1, 365);
    const d = whole(invoiceDay, 1, 31);
    const r = whole(reminder, 0, 28);
    const rm = allowance.trim() ? Number(allowance) : null;
    const bad =
      w === undefined
        ? 'Submit within: 1 to 365 days, or empty'
        : t === undefined
          ? 'Pays in: 1 to 365 days, or empty'
          : d === undefined
            ? 'Invoice day: 1 to 31, or empty for no monthly invoice'
            : r === undefined || r === null
              ? 'Reminder: 0 to 28 days before'
              : email.trim() && !EMAIL.test(email.trim())
                ? 'Check the billing email'
                : rm !== null && !(Number.isFinite(rm) && rm >= 0)
                  ? 'Check the allowance'
                  : null;
    if (bad) return toast.show({ tone: 'error', title: bad });
    const clinicCodes = Object.fromEntries(
      Object.entries(codes)
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v),
    );
    save
      .mutateAsync({
        windowDays: w,
        termsDays: t,
        invoiceDay: d,
        invoiceMode,
        reminderDaysBefore: r,
        submissionMethod: method,
        clinicCodes,
        exclusions: exclusions.trim() || null,
        contactEmail: email.trim() || null,
        ...(panel.kind === 'company'
          ? { allowanceSen: rm === null ? null : Math.round(rm * 100) }
          : {}),
      })
      .then(() => {
        toast.show({ tone: 'success', title: 'Contract saved' });
        onDone();
      })
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: 'Not saved',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  };

  return (
    <div className={local.form}>
      <Field label="Submit claims within (days after the visit)">
        <Input
          inputMode="numeric"
          value={windowDays}
          onChange={(e) => setWindow(e.target.value)}
        />
      </Field>
      <Field label="Pays in (days after the invoice)">
        <Input
          inputMode="numeric"
          value={termsDays}
          onChange={(e) => setTerms(e.target.value)}
        />
      </Field>
      <Field
        label="Monthly invoice day"
        hint="1 to 31 (31 = the last day of shorter months). Empty: no monthly invoice."
      >
        <Input
          inputMode="numeric"
          value={invoiceDay}
          onChange={(e) => setInvoiceDay(e.target.value)}
        />
      </Field>
      <ChipToggle
        label="After building the invoice"
        single
        options={[
          { value: 'review', label: 'Review first' },
          { value: 'auto', label: 'Send automatically' },
        ]}
        selected={[invoiceMode]}
        onChange={(v) => v[0] && setMode(v[0])}
      />
      <Field label="Reminder (days before the invoice day)">
        <Input
          inputMode="numeric"
          value={reminder}
          onChange={(e) => setReminder(e.target.value)}
        />
      </Field>
      <ChipToggle
        label="Send claims by"
        single
        options={[
          { value: 'portal', label: 'Portal' },
          { value: 'email', label: 'Email' },
          { value: 'export', label: 'Export file' },
        ]}
        selected={[method]}
        onChange={(v) => v[0] && setMethod(v[0])}
      />
      {me.branches.map((b) => (
        <Field key={b.id} label={`Clinic code · ${b.name}`}>
          <Input
            className="mono"
            value={codes[b.code] ?? ''}
            maxLength={40}
            onChange={(e) => setCodes({ ...codes, [b.code]: e.target.value })}
          />
        </Field>
      ))}
      <Field label="Not covered (exclusions)">
        <textarea
          className={local.textarea}
          value={exclusions}
          maxLength={500}
          onChange={(e) => setExclusions(e.target.value)}
        />
      </Field>
      <Field label="Billing email">
        <Input
          type="email"
          value={email}
          maxLength={160}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      {panel.kind === 'company' && (
        <Field label="Allowance per employee a year (RM)">
          <Input
            inputMode="decimal"
            value={allowance}
            onChange={(e) => setAllowance(e.target.value)}
          />
        </Field>
      )}
      <div className={local.acts}>
        <Button onClick={onDone}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={submit}>
          Save contract
        </Button>
      </div>
    </div>
  );
}
